<template>
  <el-container class="workspace-page companies-page">
    <el-main class="workspace-content">
      <div class="company-filter-bar">
        <el-checkbox v-model="active" @change="onFilterChange">{{ $t("只看活跃企业") }}</el-checkbox>
        <div class="company-filter-date company-filter-date--desktop">
          <span class="company-filter-label">{{ $t("创建日期") }}</span>
          <el-date-picker
              v-model="createdDateRange"
              class="company-filter-range"
              type="daterange"
              value-format="yyyy-MM-dd"
              :range-separator="$t('至')"
              :start-placeholder="$t('开始日期')"
              :end-placeholder="$t('结束日期')"
              :picker-options="createdDatePickerOptions"
              popper-class="responsive-date-picker"
              unlink-panels
              @change="onCreatedDateChange"
          />
        </div>
        <fieldset class="company-filter-date company-filter-date--mobile">
          <legend class="company-filter-label">{{ $t("创建日期") }}</legend>
          <label class="company-native-date">
            <span>{{ $t("开始日期") }}</span>
            <input
                v-model="mobileCreatedDateStart"
                type="date"
                :min="minCalendarDateText"
                :max="mobileCreatedDateEnd || todayText"
                @change="onMobileCreatedDateChange"
            >
          </label>
          <span class="company-native-date-separator">{{ $t("至") }}</span>
          <label class="company-native-date">
            <span>{{ $t("结束日期") }}</span>
            <input
                v-model="mobileCreatedDateEnd"
                type="date"
                :min="mobileCreatedDateStart || minCalendarDateText"
                :max="todayText"
                @change="onMobileCreatedDateChange"
            >
          </label>
        </fieldset>
        <el-button
            v-if="createdDateRange && createdDateRange.length"
            type="text"
            class="company-filter-clear"
            @click="clearCreatedDateRange">{{ $t("清除") }}</el-button>
        <span class="company-filter-count">{{ totalCountText }}</span>
      </div>
      <div class="company-cards">
        <el-card
            v-for="company in companies"
            :key="company.companyId"
            class="company-card"
            shadow="never">
          <div class="company-card-header">
            <div class="company-card-name">{{ company.name || '—' }}</div>
            <el-tag v-if="company.vip" type="success" size="mini">VIP</el-tag>
          </div>
          <div class="company-card-row">
            <span class="company-card-label">{{ $t("注册人") }}</span>
            <span class="company-card-value">{{ company.registerName || '—' }}</span>
          </div>
          <div class="company-card-row">
            <span class="company-card-label">{{ $t("创建") }}</span>
            <span class="company-card-value">{{ company.dateCreated || '—' }}</span>
          </div>
          <div class="company-card-row">
            <span class="company-card-label">{{ $t("最近活跃") }}</span>
            <span class="company-card-value">{{ formatReadableDate(company.lastActiveAt) }}</span>
          </div>
          <div class="company-card-row">
            <span class="company-card-label">{{ $t("近3个月问题") }}</span>
            <span class="company-card-value">{{ Number.isFinite(company.recentIssueCount) ? company.recentIssueCount : '—' }}</span>
          </div>
        </el-card>
        <div v-if="!companies.length && !loading" class="company-card-empty">{{ $t("暂无企业数据") }}</div>
      </div>
      <div class="load-more">
        <i v-if="loading" class="el-icon-loading load-more-spinner"></i>
        <span v-else-if="!hasMore" class="load-more-tip">{{ $t("已加载全部") }}</span>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { t } from "@/i18n";
import axios from 'axios';

export default {
  name: 'CompanyList',
  data() {
    return {
      companies: [],
      loading: false,
      pageSize: 20,
      nextCursor: null,
      hasMore: true,
      active: true,
      createdDateRange: null,
      mobileCreatedDateStart: '',
      mobileCreatedDateEnd: '',
      minCalendarDate: null,
      totalCount: null,
      countLoading: false,
    };
  },
  created() {
    const now = new Date();
    this.minCalendarDate = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
    this.scrollHandler = () => {
      if (!this.hasMore || this.loading) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const fullHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
      if (scrollTop + viewportHeight >= fullHeight - 120) {
        this.fetchCompanies(false);
      }
    };
    window.addEventListener('scroll', this.scrollHandler);
    this.fetchCompanies(true);
    this.fetchCount();
  },
  beforeDestroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  },
  computed: {
    totalCountText() {
      if (this.countLoading) return t("统计中...");
      if (this.totalCount === null || this.totalCount === undefined) return t("统计失败");
      return t("共 {p0} 家", { p0: this.totalCount });
    },
    minCalendarDateText() {
      return this.formatDate(this.minCalendarDate);
    },
    todayText() {
      return this.formatDate(new Date());
    },
    createdDatePickerOptions() {
      const minTime = this.minCalendarDate ? this.minCalendarDate.getTime() : 0;
      const now = new Date();
      const maxTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
      return {
        disabledDate(date) {
          const time = date.getTime();
          return time < minTime || time > maxTime;
        },
      };
    },
  },
  methods: {
    formatDate(value) {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatReadableDate(value) {
      if (!value) return '—';
      const parsed = this.parseDate(value);
      if (!parsed) return value;
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      const diffMs = startOfToday.getTime() - startOfDate.getTime();
      const days = Math.floor(diffMs / 86400000);
      if (days < 0) return value;
      if (days === 0) return t("今天");
      if (days === 1) return t("昨天");
      if (days === 2) return t("前天");
      if (days < 30) return t("{p0}天前", { p0: days });
      if (days < 365) return t("{p0}个月前", { p0: Math.max(1, Math.floor(days / 30)) });
      return t("{p0}年前", { p0: Math.max(1, Math.floor(days / 365)) });
    },
    parseDate(value) {
      if (value instanceof Date) return value;
      const raw = String(value).trim();
      if (!raw) return null;
      let normalized = raw;
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        normalized = `${raw}T00:00:00`;
      } else if (raw.includes(' ') && !raw.includes('T')) {
        normalized = raw.replace(' ', 'T');
      }
      const parsed = new Date(normalized);
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed;
    },
    fetchCompanies(reset) {
      if (this.loading) return;
      if (!reset && !this.hasMore) return;
      this.loading = true;
      if (reset) {
        this.companies = [];
        this.nextCursor = null;
        this.hasMore = true;
      }
      axios
          .get(process.env.VUE_APP_BASE_URL + '/companies/enterprise-list', {
            params: {
              pageSize: this.pageSize,
              lastActiveAt: this.nextCursor?.lastActiveAt,
              lastCompanyId: this.nextCursor?.lastCompanyId,
              active: this.active,
              ...this.getDateRangeParams(),
            },
          })
          .then((res) => {
            const payload = res.data || {};
            const records = Array.isArray(payload.records) ? payload.records : [];
            this.companies = reset ? records : this.companies.concat(records);
            this.nextCursor = payload.nextCursor || null;
            this.hasMore = Boolean(this.nextCursor && records.length > 0);
          })
          .catch((error) => {
            const status = error?.response?.status;
            const msg = error?.response?.data?.message || t("获取企业列表失败");
            this.$message.error(msg);
            if (status === 401 || status === 403) {
              this.$router.replace({path: '/login'});
            }
          })
          .finally(() => {
            this.loading = false;
          });
    },
    fetchCount() {
      if (this.countLoading) return;
      this.countLoading = true;
      axios
          .get(process.env.VUE_APP_BASE_URL + '/companies/enterprise-list/count', {
            params: {
              active: this.active,
              ...this.getDateRangeParams(),
            },
          })
          .then((res) => {
            const payload = res.data || {};
            const total = Number(payload.total);
            this.totalCount = Number.isFinite(total) ? total : 0;
          })
          .catch(() => {
            this.totalCount = null;
          })
          .finally(() => {
            this.countLoading = false;
          });
    },
    getDateRangeParams() {
      if (!Array.isArray(this.createdDateRange) || this.createdDateRange.length !== 2) {
        return {};
      }
      const [startDate, endDate] = this.createdDateRange;
      return {
        startDate,
        endDate,
      };
    },
    onCreatedDateChange(dates) {
      if (Array.isArray(dates) && dates.length === 2) {
        this.mobileCreatedDateStart = dates[0];
        this.mobileCreatedDateEnd = dates[1];
      } else {
        this.createdDateRange = null;
        this.mobileCreatedDateStart = '';
        this.mobileCreatedDateEnd = '';
      }
      this.onFilterChange();
    },
    onMobileCreatedDateChange() {
      const start = this.mobileCreatedDateStart;
      const end = this.mobileCreatedDateEnd;
      if (!start || !end) {
        if (this.createdDateRange) {
          this.createdDateRange = null;
          this.onFilterChange();
        }
        return;
      }
      this.createdDateRange = [start, end];
      this.onFilterChange();
    },
    clearCreatedDateRange() {
      this.createdDateRange = null;
      this.mobileCreatedDateStart = '';
      this.mobileCreatedDateEnd = '';
      this.onFilterChange();
    },
    onFilterChange() {
      this.fetchCompanies(true);
      this.fetchCount();
    },
  },
};
</script>

<style scoped>
.company-filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.company-filter-range {
  margin-left: 0;
  width: 360px;
}

.company-filter-date {
  margin: 0;
}

.company-filter-date--desktop {
  display: flex;
  align-items: center;
  gap: 10px;
}

.company-filter-date--mobile {
  display: none;
}

.company-filter-label {
  flex: 0 0 auto;
  color: var(--text-regular);
  font-size: 14px;
}

.company-filter-clear {
  margin-left: 0;
  padding: 0;
}

.company-filter-count {
  margin-left: auto;
  color: var(--text-muted);
  font-size: 13px;
}

.company-list-card {
  margin: 16px;
}

.company-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.company-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.company-card {
  border: 1px solid var(--border-light);
}

.company-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.company-card-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.company-card-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 14px;
}

.company-card-label {
  color: var(--text-muted);
}

.company-card-value {
  color: var(--text-primary);
}

.company-card-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 16px 0 8px;
}

@media (max-width: 768px) {
  .company-filter-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .company-filter-date--desktop {
    display: none;
  }

  .company-filter-date--mobile {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: end;
    gap: 8px;
    border: 0;
    padding: 0;
  }

  .company-filter-date--mobile .company-filter-label {
    grid-column: 1 / -1;
    padding: 0;
  }

  .company-native-date {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;
    color: var(--text-muted);
    font-size: 12px;
  }

  .company-native-date input {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 44px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 0 10px;
    background: var(--surface-color);
    color: var(--text-primary);
    font: inherit;
    font-size: 16px;
    color-scheme: var(--native-color-scheme);
  }

  .company-native-date-separator {
    padding-bottom: 13px;
    color: var(--text-muted);
  }

  .company-filter-count {
    margin-left: 0;
  }

  .company-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 420px) {
  .company-filter-date--mobile {
    grid-template-columns: 1fr;
  }

  .company-filter-date--mobile .company-filter-label {
    grid-column: auto;
  }

  .company-native-date-separator {
    display: none;
  }
}

.load-more {
  display: flex;
  justify-content: center;
  padding: 16px 0 8px;
}
.load-more-spinner {
  font-size: 18px;
  color: #909399;
}

.load-more-tip {
  color: #909399;
  font-size: 12px;
}
</style>
